import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "qp_chat_session_id";

const getOrCreateSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `chat_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const GREETING = {
  role: "assistant",
  text: "Hi there \ud83d\udc4b I'm QP Assistant, Quality Power's virtual assistant. Are you an existing customer, or a new customer exploring our products?",
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionIdRef = useRef(getOrCreateSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await fetch(`${API}/chat/history?session_id=${sessionIdRef.current}`);
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages.map((m) => ({ role: m.role, text: m.text })));
        }
      } catch (e) {
        // silent - fall back to greeting
      }
    };
    loadHistory();
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }]);
    setStreaming(true);
    setMessages((prev) => [...prev, { role: "assistant", text: "" }]);

    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionIdRef.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", text: next[next.length - 1].text + payload.delta };
              return next;
            });
          }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { role: "assistant", text: "Sorry, I ran into a connection issue. Please try again." };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close chat" : "Open chat"}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full btn-glow-blue flex items-center justify-center shadow-lg transition-transform hover:scale-105"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[560px] glass-panel rounded-2xl flex flex-col overflow-hidden border-qcyan/30">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border/10 bg-white/[0.02]">
            <span className="w-9 h-9 rounded-full bg-qcyan/10 border border-qcyan/30 flex items-center justify-center">
              <Sparkles size={16} className="text-qcyan" />
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground font-display">QP Assistant</p>
              <p className="text-xs text-muted-foreground">Ask about products & solutions</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 no-scrollbar">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                    m.role === "user"
                      ? "bg-qcyan/15 border border-qcyan/30 text-foreground"
                      : "bg-white/5 border border-border/10 text-muted-foreground"
                  }`}
                >
                  {m.text || (streaming && i === messages.length - 1 ? "\u2026" : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-border/10 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              className="flex-1 bg-white/5 border border-border/10 rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-qcyan/50 transition-colors"
            />
            <button
              onClick={sendMessage}
              disabled={streaming || !input.trim()}
              className="w-10 h-10 rounded-lg btn-glow-primary flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              aria-label="Send message"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
